import { DocumentNode, QueryHookOptions, useQuery } from '@apollo/client'
import { useLocation } from 'react-router-dom'

import { IncorrectLoggedInUserError, NetworkError } from '../lib/custom-errors'
import getPhoenixUrlParams from '../lib/get-phoenix-url-params'

interface Result<Q> {
  orderData?: Q
  isOrderLoading: boolean
}

interface OrderQueryVariables {
  orderId: string
}

const useOrder = <Q>(query: DocumentNode, options?: QueryHookOptions<Q, OrderQueryVariables>): Result<Q> => {
  const location = useLocation()
  const { orderId } = getPhoenixUrlParams(location.search)

  const {
    data,
    loading: isOrderLoading,
    error,
  } = useQuery<Q, OrderQueryVariables>(query, {
    ...options,
    variables: { orderId: orderId as string },
    skip: !orderId,
  })

  const orderData = data as Q | undefined

  if (error) {
    if (error.networkError) {
      throw new NetworkError(
        'There was a connection error when trying to read the order data',
        error.networkError.message,
      )
    }

    if (error.graphQLErrors.some(({ extensions }) => extensions?.status === 401)) {
      throw new IncorrectLoggedInUserError('The currently logged in user is not the owner of this order', error.message)
    }

    throw new Error(`${error.name} - ${error.message}`)
  }

  return { orderData, isOrderLoading }
}

export default useOrder
